import { getAlbums, addMediaToAlbum } from '@/app/services/albums/albums';
import * as Actions from '@/app/store/actions';
import ModalBase from '@/app/customComponents/modals/ModalBase';
import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import { CircularProgress } from '@material-ui/core';
import { Search as SearchIcon, Add as AddIcon } from '@material-ui/icons';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

const AddToAlbumModal = ({ open, setOpen, mediaIds = [], onComplete, onCreateNew }) => {
	const [albums, setAlbums] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [selectedAlbumId, setSelectedAlbumId] = useState(null);
	const [searchQuery, setSearchQuery] = useState('');
	const dispatch = useDispatch();
	const debounceTimer = useRef(null);

	const fetchAlbums = useCallback(async (search = '') => {
		try {
			setLoading(true);
			const params = search
				? { search, sort: 'updated' }
				: { sort: 'updated', per_page: 3 };
			const res = await getAlbums(params);
			setAlbums(res.data.data || []);
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to fetch albums', variant: 'error' }));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (open) {
			setSearchQuery('');
			setSelectedAlbumId(null);
			fetchAlbums();
		}
	}, [open, fetchAlbums]);

	const handleSearchChange = (value) => {
		setSearchQuery(value);
		if (debounceTimer.current) clearTimeout(debounceTimer.current);
		debounceTimer.current = setTimeout(() => {
			fetchAlbums(value.trim());
		}, 300);
	};

	const handleAddToAlbum = async () => {
		if (!selectedAlbumId || mediaIds.length === 0) return;

		setSaving(true);
		try {
			await addMediaToAlbum(selectedAlbumId, mediaIds);
			dispatch(
				Actions.showMessage({
					message: `${mediaIds.length} item${mediaIds.length !== 1 ? 's' : ''} added to album`,
					variant: 'success',
				})
			);
			setSelectedAlbumId(null);
			setSearchQuery('');
			setOpen(false);
			if (onComplete) onComplete();
		} catch (error) {
			const message = error?.response?.data?.message || 'Failed to add media to album';
			dispatch(Actions.showMessage({ message, variant: 'error' }));
		} finally {
			setSaving(false);
		}
	};

	return (
		<ModalBase maxWidth={480} open={open} setOpen={setOpen} showClose={false}>
			<h2 className="text-20 font-bold text-gray-900 mb-4">Add to Album</h2>
			<p className="text-14 text-gray-500 mb-16">
				{mediaIds.length} item{mediaIds.length !== 1 ? 's' : ''} selected
			</p>

			{/* Search */}
			<div className="relative mb-16">
				<SearchIcon
					className="absolute left-12 top-1/2 transform -translate-y-1/2 text-gray-400"
					style={{ fontSize: 20 }}
				/>
				<input
					className="w-full pl-40 pr-12 py-10 border border-gray-300 rounded-md text-14 outline-none focus:border-blue-500"
					onChange={(e) => handleSearchChange(e.target.value)}
					placeholder="Search albums..."
					type="text"
					value={searchQuery}
				/>
			</div>

			{/* Create New Album button */}
			<button
				className="w-full flex items-center gap-12 p-12 mb-8 rounded-md border border-dashed border-gray-300 text-14 font-medium text-blue-600 hover:bg-blue-50 transition-colors"
				onClick={() => {
					setOpen(false);
					if (onCreateNew) onCreateNew();
				}}
				type="button"
			>
				<AddIcon style={{ fontSize: 20 }} />
				Create New Album
			</button>

			{/* Album List */}
			<div className="add-to-album-list">
				{loading ? (
					<div className="flex justify-center py-32">
						<CircularProgress size={32} />
					</div>
				) : albums.length === 0 ? (
					<div className="text-center py-32 text-gray-500 text-14">
						{searchQuery ? 'No albums match your search' : 'No albums yet'}
					</div>
				) : (
					<>
						{!searchQuery && (
							<div className="text-12 text-gray-400 px-12 mb-4">Recent albums</div>
						)}
						{albums.map((album) => (
							<div
								className={`add-to-album-item ${selectedAlbumId === album.id ? 'selected' : ''}`}
								key={album.id}
								onClick={() => setSelectedAlbumId(album.id)}
							>
								{album.cover_image ? (
									<img alt="" className="album-thumb" src={album.cover_image} />
								) : (
									<div className="album-thumb" />
								)}
								<div className="album-info">
									<h4>{album.name}</h4>
									<span>
										{album.media_count || 0} item{album.media_count !== 1 ? 's' : ''}
										{album.room ? ` • ${album.room.name}` : ''}
									</span>
								</div>
							</div>
						))}
					</>
				)}
			</div>

			<div className="flex justify-end gap-12 mt-20">
				<CustomButton
					onClick={() => {
						setSelectedAlbumId(null);
						setSearchQuery('');
						setOpen(false);
					}}
					variant="secondary"
				>
					Cancel
				</CustomButton>
				<CustomButton disabled={saving || !selectedAlbumId} onClick={handleAddToAlbum}>
					{saving ? <CircularProgress size={20} /> : 'Add to Album'}
				</CustomButton>
			</div>
		</ModalBase>
	);
};

export default AddToAlbumModal;
